import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  GripVertical,
  ImagePlus,
  Trash2,
  Type,
  AlignLeft,
  FilePlus,
  type LucideIcon,
} from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import type { LeadPosition, PositionRowId } from '../../../types';
import { OfferPositionPickerPanel } from '../../leads/components/OfferPositionPickerPanel';
import { parseGermanNumber } from '../../../utils/parseGermanNumber';
import { stammdatenPath } from '../../../utils/stammdatenRoutes';
import { useNavigateBack } from '../../../hooks/useNavigateBack';
import type { OfferingVorlage } from '../types';

const eurFmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const TABLE_COL_SPAN = 6;
const VORLAGE_FIELD_CLASS =
  'w-full border border-app-border rounded-md px-2 py-1 text-sm outline-none focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20';

type BlockKind = 'text' | 'title' | 'freePosition' | 'image';

function normalizePositions(data: unknown): LeadPosition[] {
  if (!Array.isArray(data)) return [];
  return data.map((row) => {
    const r = row as LeadPosition;
    return { ...r, item_name: r.item_name ?? '', description: r.description ?? '', internal_note: r.internal_note ?? '' };
  });
}

function isBlockRow(p: LeadPosition): boolean {
  const st = String(p.source_type ?? '');
  return st === 'custom_title' || st === 'custom_text' || st === 'custom_image';
}

function blockKindToSourceType(kind: BlockKind): LeadPosition['source_type'] {
  if (kind === 'title') return 'custom_title';
  if (kind === 'text') return 'custom_text';
  if (kind === 'image') return 'custom_image';
  return 'custom_free';
}

function DropBand({ id, colSpan }: { id: string; colSpan: number }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  return (
    <tr ref={setNodeRef}>
      <td colSpan={colSpan} className="p-0">
        <div
          className={`h-2 my-0.5 rounded ${isOver ? 'bg-pipedrive-blue/30' : 'bg-transparent border border-dashed border-app-border'}`}
        />
      </td>
    </tr>
  );
}

type VorlageRowDragHandleProps = {
  setNodeRef: (node: HTMLTableRowElement | null) => void;
  style: React.CSSProperties;
  attributes: ReturnType<typeof useDraggable>['attributes'];
  listeners: ReturnType<typeof useDraggable>['listeners'];
};

function DraggableVorlageRow({
  id,
  rowId,
  children,
}: {
  id: string;
  rowId: string;
  children: (props: VorlageRowDragHandleProps) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id,
    data: { source: 'row', rowId },
  });
  const { setNodeRef: setDropRef } = useDroppable({ id });
  const setNodeRef = React.useCallback(
    (node: HTMLTableRowElement | null) => {
      setDragRef(node);
      setDropRef(node);
    },
    [setDragRef, setDropRef],
  );
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.6 : 1,
  };
  return <>{children({ setNodeRef, style, attributes, listeners })}</>;
}

function VorlageBlockPaletteItem({
  id,
  kind,
  label,
  icon: Icon,
  disabled,
  onClick,
}: {
  id: string;
  kind: BlockKind;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
  onClick?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: { source: 'block', kind },
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.65 : 1,
  };
  return (
    <button
      type="button"
      ref={setNodeRef}
      style={style}
      disabled={disabled}
      onClick={onClick}
      {...attributes}
      {...listeners}
      className="flex items-center gap-1.5 px-2 py-2 rounded-lg border border-app-border text-xs font-semibold hover:bg-app-hover disabled:opacity-50 cursor-grab active:cursor-grabbing"
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

type VorlageEditorProps = {
  vorlageId: string;
};

export const VorlageEditor: React.FC<VorlageEditorProps> = ({ vorlageId }) => {
  const navigate = useNavigate();
  const { instanceId: instanceIdParam } = useParams<{ instanceId: string }>();
  const instanceId = instanceIdParam ?? 'default';
  const goBack = useNavigateBack(stammdatenPath(instanceId, 'vorlagen'));
  const [vorlage, setVorlage] = React.useState<OfferingVorlage | null>(null);
  const [positions, setPositions] = React.useState<LeadPosition[]>([]);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [savingId, setSavingId] = React.useState<PositionRowId | null>(null);
  const [addingBlock, setAddingBlock] = React.useState(false);
  const [mobilePickerOpen, setMobilePickerOpen] = React.useState(false);
  const [activeDragLabel, setActiveDragLabel] = React.useState<string | null>(null);
  const imageInputRef = React.useRef<HTMLInputElement | null>(null);

  const positionsApiBase = `/api/offering-vorlagen/${encodeURIComponent(vorlageId)}/positions`;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const reloadPositions = React.useCallback(async () => {
    try {
      const pRes = await fetch(positionsApiBase);
      if (!pRes.ok) throw new Error('Positionen konnten nicht geladen werden.');
      setPositions(normalizePositions(await pRes.json()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    }
  }, [positionsApiBase]);

  const loadInitial = React.useCallback(async () => {
    setInitialLoading(true);
    setError(null);
    try {
      const [vRes, pRes] = await Promise.all([
        fetch(`/api/offering-vorlagen/${encodeURIComponent(vorlageId)}`),
        fetch(positionsApiBase),
      ]);
      if (!vRes.ok) throw new Error('Vorlage nicht gefunden.');
      if (!pRes.ok) throw new Error('Positionen konnten nicht geladen werden.');
      setVorlage((await vRes.json()) as OfferingVorlage);
      setPositions(normalizePositions(await pRes.json()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setInitialLoading(false);
    }
  }, [vorlageId, positionsApiBase]);

  React.useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const saveRow = async (p: LeadPosition, patch: Partial<LeadPosition>) => {
    const body: Record<string, unknown> = {};
    if (patch.quantity !== undefined) body.quantity = patch.quantity;
    if (patch.unit !== undefined) body.unit = patch.unit;
    if (patch.item_name !== undefined) body.item_name = patch.item_name;
    if (patch.description !== undefined) body.description = patch.description;
    if (patch.unit_cost_ek !== undefined) body.unit_cost_ek = patch.unit_cost_ek;
    if (patch.surcharge_percent !== undefined) body.surcharge_percent = patch.surcharge_percent;
    if (patch.unit_price_vk !== undefined) body.unit_price_vk = patch.unit_price_vk;
    if (Object.keys(body).length === 0) return;

    setSavingId(p.id);
    try {
      const res = await fetch(`${positionsApiBase}/${encodeURIComponent(String(p.id))}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Speichern fehlgeschlagen');
      const updated = (await res.json()) as LeadPosition;
      setPositions((prev) =>
        prev.map((row) => (row.id === updated.id ? { ...updated, item_name: updated.item_name ?? '' } : row)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
      await reloadPositions();
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (id: PositionRowId) => {
    try {
      const res = await fetch(`${positionsApiBase}/${encodeURIComponent(String(id))}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Löschen fehlgeschlagen');
      setPositions((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    }
  };

  const persistRowOrder = React.useCallback(
    async (ordered: LeadPosition[]) => {
      setPositions(ordered);
      try {
        const res = await fetch(`${positionsApiBase}/order`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order: ordered.map((p) => String(p.id)) }),
        });
        if (!res.ok) throw new Error('Reihenfolge konnte nicht gespeichert werden');
        setPositions(normalizePositions(await res.json()));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Fehler');
        await reloadPositions();
      }
    },
    [reloadPositions, positionsApiBase],
  );

  const insertBlockAt = React.useCallback(
    async (kind: BlockKind, insertIndex?: number, extra?: Record<string, unknown>) => {
      const sourceType = blockKindToSourceType(kind);
      setAddingBlock(true);
      setError(null);
      try {
        const body: Record<string, unknown> = {
          source_type: sourceType,
          item_name: '',
          description: '',
          quantity: sourceType === 'custom_free' ? 1 : 0,
          unit: sourceType === 'custom_title' ? 'Titel' : sourceType === 'custom_free' ? 'Stk' : '',
          unit_cost_ek: 0,
          surcharge_percent: 0,
          unit_price_vk: 0,
          ...extra,
        };
        if (typeof insertIndex === 'number') body.sort_order = Math.max(0, insertIndex);
        const res = await fetch(positionsApiBase, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const j = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(j.error ?? 'Hinzufügen fehlgeschlagen');
        }
        await reloadPositions();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Fehler');
      } finally {
        setAddingBlock(false);
      }
    },
    [reloadPositions, positionsApiBase],
  );

  const insertPickerAt = React.useCallback(
    async (kind: 'material' | 'leistung', id: string, insertIndex?: number) => {
      setError(null);
      try {
        const body: Record<string, unknown> = kind === 'material' ? { material_id: id } : { leistung_id: id };
        if (typeof insertIndex === 'number') body.sort_order = Math.max(0, insertIndex);
        const res = await fetch(positionsApiBase, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const j = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(j.error ?? 'Hinzufügen fehlgeschlagen');
        }
        await reloadPositions();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Fehler');
      }
    },
    [reloadPositions, positionsApiBase],
  );

  const addBlock = React.useCallback(
    (sourceType: 'custom_title' | 'custom_text' | 'custom_free' | 'custom_image', extra?: Record<string, unknown>) => {
      const kind: BlockKind =
        sourceType === 'custom_title'
          ? 'title'
          : sourceType === 'custom_text'
            ? 'text'
            : sourceType === 'custom_image'
              ? 'image'
              : 'freePosition';
      void insertBlockAt(kind, positions.length, extra);
    },
    [insertBlockAt, positions.length],
  );

  const onImageSelected = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Nur Bilddateien werden unterstützt.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : '';
      addBlock('custom_image', {
        image: { src, alt: file.name },
        item_name: file.name,
      });
    };
    reader.readAsDataURL(file);
  };

  const onTableDragStart = React.useCallback((event: DragStartEvent) => {
    const source = event.active.data.current?.source;
    if (source === 'block') {
      const kind = String(event.active.data.current?.kind ?? '');
      const label =
        kind === 'title' ? 'Titel' : kind === 'freePosition' ? 'Freie Position' : kind === 'image' ? 'Bild' : 'Text';
      setActiveDragLabel(label);
      return;
    }
    if (source === 'picker') {
      setActiveDragLabel('Material/Leistung');
      return;
    }
    if (source === 'row') {
      setActiveDragLabel('Zeile verschieben');
      return;
    }
    setActiveDragLabel(null);
  }, []);

  const onTableDragCancel = React.useCallback(() => {
    setActiveDragLabel(null);
  }, []);

  const onTableDragEnd = React.useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      const overId = String(over.id);
      let dropIndex: number | null = null;
      if (overId.startsWith('drop-')) {
        const parsed = Number(overId.replace('drop-', ''));
        if (Number.isFinite(parsed)) dropIndex = parsed;
      } else if (overId.startsWith('row-')) {
        const overRowId = overId.replace('row-', '');
        const rowIndex = positions.findIndex((p) => String(p.id) === overRowId);
        if (rowIndex >= 0) dropIndex = rowIndex;
      }
      if (dropIndex == null) return;

      const source = active.data.current?.source;
      setError(null);
      try {
        if (source === 'block') {
          await insertBlockAt(active.data.current?.kind as BlockKind, dropIndex);
          return;
        }
        if (source === 'picker') {
          await insertPickerAt(
            active.data.current?.kind as 'material' | 'leistung',
            String(active.data.current?.id ?? ''),
            dropIndex,
          );
          return;
        }
        if (source === 'row') {
          const rowId = String(active.data.current?.rowId ?? '');
          const from = positions.findIndex((p) => String(p.id) === rowId);
          if (from < 0) return;
          const next = [...positions];
          const [moved] = next.splice(from, 1);
          const insertAt = dropIndex > from ? dropIndex - 1 : dropIndex;
          next.splice(Math.max(0, Math.min(next.length, insertAt)), 0, moved);
          await persistRowOrder(next);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Fehler');
      }
    },
    [insertBlockAt, insertPickerAt, persistRowOrder, positions],
  );

  const handlePickerInsert = React.useCallback(
    (item: {
      kind: 'material' | 'leistung';
      id: string;
      insertIndex?: number;
    }) => insertPickerAt(item.kind, item.id, typeof item.insertIndex === 'number' ? item.insertIndex : positions.length),
    [insertPickerAt, positions.length],
  );

  const netSum = positions.reduce(
    (sum, p) => sum + (Number.isFinite(p.line_total) ? p.line_total : p.quantity * p.unit_price_vk),
    0,
  );

  const renderPositionRow = (p: LeadPosition, dragProps?: VorlageRowDragHandleProps) => {
    const block = isBlockRow(p);
    const busy = savingId === p.id;
    const st = String(p.source_type ?? '');
    const isImage = st === 'custom_image';

    const rowContent = (
      <>
        <td className="w-8 px-1 py-2 align-top">
          {dragProps ? (
            <button
              type="button"
              className="p-1 rounded text-app-text-subtle hover:text-app-text-muted cursor-grab active:cursor-grabbing"
              {...dragProps.attributes}
              {...dragProps.listeners}
              aria-label="Reihenfolge ändern"
            >
              <GripVertical className="w-4 h-4" />
            </button>
          ) : null}
        </td>
        <td className="px-2 py-2 align-top" colSpan={block && !isImage ? 4 : 1}>
          {isImage ? (
            <div className="space-y-1">
              <input
                type="text"
                defaultValue={p.item_name}
                disabled={busy}
                placeholder="Bildtitel"
                onBlur={(e) => {
                  if (e.target.value !== p.item_name) void saveRow(p, { item_name: e.target.value });
                }}
                className={VORLAGE_FIELD_CLASS}
              />
              {p.description?.startsWith('data:image') || p.internal_note?.includes('__image__:') ? (
                <p className="text-xs text-app-text-subtle">Bild gespeichert</p>
              ) : null}
            </div>
          ) : block ? (
            <textarea
              defaultValue={p.item_name || p.description || ''}
              disabled={busy}
              rows={st === 'custom_title' ? 1 : 3}
              onBlur={(e) => {
                const val = e.target.value;
                void saveRow(p, st === 'custom_title' ? { item_name: val } : { description: val, item_name: val.slice(0, 80) });
              }}
              className={`${VORLAGE_FIELD_CLASS} ${st === 'custom_title' ? 'font-semibold' : ''}`}
              placeholder={st === 'custom_title' ? 'Titel…' : 'Text…'}
            />
          ) : (
            <div className="space-y-1">
              <input
                type="text"
                defaultValue={p.item_name}
                disabled={busy}
                onBlur={(e) => {
                  if (e.target.value !== p.item_name) void saveRow(p, { item_name: e.target.value });
                }}
                placeholder="Kurztext"
                className={`${VORLAGE_FIELD_CLASS} font-medium`}
              />
              <textarea
                defaultValue={p.description ?? ''}
                disabled={busy}
                rows={2}
                onBlur={(e) => {
                  if (e.target.value !== (p.description ?? '')) void saveRow(p, { description: e.target.value });
                }}
                placeholder="Beschreibung"
                className={`${VORLAGE_FIELD_CLASS} text-app-text-muted resize-y min-h-[2.5rem]`}
              />
            </div>
          )}
        </td>
        {!block || isImage ? (
          <>
            {!isImage ? (
              <>
                <td className="px-2 py-2 align-top">
                  <input
                    type="text"
                    defaultValue={String(p.quantity)}
                    disabled={busy}
                    onBlur={(e) => {
                      const n = parseGermanNumber(e.target.value);
                      if (n !== p.quantity) void saveRow(p, { quantity: n });
                    }}
                    className={`${VORLAGE_FIELD_CLASS} text-right tabular-nums`}
                  />
                </td>
                <td className="px-2 py-2 align-top">
                  <input
                    type="text"
                    defaultValue={p.unit ?? ''}
                    disabled={busy}
                    onBlur={(e) => {
                      if (e.target.value !== (p.unit ?? '')) void saveRow(p, { unit: e.target.value });
                    }}
                    className={VORLAGE_FIELD_CLASS}
                  />
                </td>
                <td className="px-2 py-2 align-top text-right">
                  <input
                    type="text"
                    defaultValue={String(p.unit_price_vk).replace('.', ',')}
                    disabled={busy}
                    onBlur={(e) => {
                      const n = parseGermanNumber(e.target.value);
                      if (n !== p.unit_price_vk) void saveRow(p, { unit_price_vk: n });
                    }}
                    className={`${VORLAGE_FIELD_CLASS} text-right tabular-nums`}
                  />
                </td>
              </>
            ) : (
              <td colSpan={3} />
            )}
          </>
        ) : null}
        <td className="px-2 py-2 align-top">
          <button
            type="button"
            onClick={() => void deleteRow(p.id)}
            className="p-1.5 rounded text-app-text-subtle hover:text-red-600 hover:bg-red-50"
            aria-label="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </td>
      </>
    );

    if (!dragProps) return rowContent;

    return (
      <tr ref={dragProps.setNodeRef} style={dragProps.style} className="bg-app-surface divide-y divide-app-border">
        {rowContent}
      </tr>
    );
  };

  if (initialLoading) {
    return (
      <InlineLoadingState label="Vorlage wird geladen…" className="py-20" />
    );
  }

  if (!vorlage) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-app-text-muted mb-4">Vorlage nicht gefunden.</p>
        <button
          type="button"
          onClick={() => navigate(stammdatenPath(instanceId, 'vorlagen'))}
          className="text-sm font-semibold text-pipedrive-blue hover:underline"
        >
          Zurück zur Liste
        </button>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onTableDragStart}
      onDragCancel={onTableDragCancel}
      onDragEnd={async (e) => {
        await onTableDragEnd(e);
        setActiveDragLabel(null);
      }}
    >
      <div className="flex flex-col flex-1 min-h-0">
        <div className="flex items-center gap-3 mb-4 shrink-0">
          <button
            type="button"
            onClick={goBack}
            className="p-2 rounded-lg text-app-text-subtle hover:bg-app-hover"
            aria-label="Zurück"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-app-text truncate">{vorlage.name}</h2>
            <p className="text-xs text-app-text-subtle">
              {positions.length} Positionen · Netto {eurFmt.format(netSum)}
            </p>
          </div>
        </div>

        {error ? (
          <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 shrink-0">{error}</div>
        ) : null}

        <div className="lg:hidden flex flex-wrap gap-2 mb-3 shrink-0">
          <button type="button" onClick={() => addBlock('custom_title')} className="btn-sm border rounded-lg px-2 py-1 text-xs font-semibold">
            Titel
          </button>
          <button type="button" onClick={() => addBlock('custom_text')} className="btn-sm border rounded-lg px-2 py-1 text-xs font-semibold">
            Text
          </button>
          <button type="button" onClick={() => addBlock('custom_free')} className="btn-sm border rounded-lg px-2 py-1 text-xs font-semibold">
            Freie Pos.
          </button>
          <button type="button" onClick={() => imageInputRef.current?.click()} className="btn-sm border rounded-lg px-2 py-1 text-xs font-semibold">
            Bild
          </button>
          <button
            type="button"
            onClick={() => setMobilePickerOpen(true)}
            className="btn-sm border rounded-md px-2 py-1 text-xs font-semibold bg-pipedrive-green text-white"
          >
            Suche
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 max-h-[calc(100dvh-11rem)]">
          <div className="app-scrollbar flex-1 min-w-0 min-h-0 overflow-auto overscroll-contain rounded-xl border border-app-border bg-app-surface">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-app-surface-muted border-b border-app-border text-left text-xs text-app-text-muted z-10">
                <tr>
                  <th className="w-8" />
                  <th className="px-2 py-2">Bezeichnung</th>
                  <th className="px-2 py-2 w-16">Menge</th>
                  <th className="px-2 py-2 w-16">Einheit</th>
                  <th className="px-2 py-2 w-24 text-right">VK</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                <DropBand id="drop-0" colSpan={TABLE_COL_SPAN} />
                {positions.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COL_SPAN} className="px-4 py-12 text-center text-app-text-subtle text-sm">
                      Noch keine Bausteine. Ziehe rechts Materialien, Leistungen oder Blöcke hierher - oder klicke zum Hinzufügen.
                    </td>
                  </tr>
                ) : (
                  positions.map((p, idx) => (
                    <React.Fragment key={String(p.id)}>
                      <DraggableVorlageRow id={`row-${p.id}`} rowId={String(p.id)}>
                        {(dragProps) => renderPositionRow(p, dragProps)}
                      </DraggableVorlageRow>
                      <DropBand id={`drop-${idx + 1}`} colSpan={TABLE_COL_SPAN} />
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

        <aside className="hidden lg:flex w-[300px] shrink-0 flex-col min-h-0 self-stretch border border-app-border rounded-xl bg-app-surface overflow-hidden">
          <div className="px-3 py-2 border-b border-app-border bg-app-surface-muted shrink-0">
            <p className="text-xs font-medium text-app-text-muted">Bausteine</p>
          </div>
          <div className="p-2 grid grid-cols-2 gap-1.5 shrink-0 border-b border-app-border">
            <VorlageBlockPaletteItem
              id="block-text"
              kind="text"
              label="Text"
              icon={AlignLeft}
              disabled={addingBlock}
              onClick={() => addBlock('custom_text')}
            />
            <VorlageBlockPaletteItem
              id="block-title"
              kind="title"
              label="Titel"
              icon={Type}
              disabled={addingBlock}
              onClick={() => addBlock('custom_title')}
            />
            <VorlageBlockPaletteItem
              id="block-free"
              kind="freePosition"
              label="Freie Pos."
              icon={FilePlus}
              disabled={addingBlock}
              onClick={() => addBlock('custom_free')}
            />
            <VorlageBlockPaletteItem
              id="block-image"
              kind="image"
              label="Bild"
              icon={ImagePlus}
              disabled={addingBlock}
              onClick={() => imageInputRef.current?.click()}
            />
          </div>
          <OfferPositionPickerPanel
            positionsApiBase={positionsApiBase}
            onAdded={reloadPositions}
            dragAndDropEnabled
            onInsertItem={handlePickerInsert}
          />
        </aside>
        </div>

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            void onImageSelected(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />

        {mobilePickerOpen ? (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/40 flex items-end">
            <div className="bg-app-surface w-full max-h-[85vh] rounded-t-xl flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-app-border">
                <span className="font-semibold text-app-text">Position hinzufügen</span>
                <button type="button" onClick={() => setMobilePickerOpen(false)} className="text-sm text-app-text-subtle">
                  Schließen
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                <OfferPositionPickerPanel
                  positionsApiBase={positionsApiBase}
                  onAdded={async () => {
                    await reloadPositions();
                    setMobilePickerOpen(false);
                  }}
                  dragAndDropEnabled
                  onInsertItem={handlePickerInsert}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {activeDragLabel ? (
        <DragOverlay>
          <div className="rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-text shadow-lg">
            {activeDragLabel}
          </div>
        </DragOverlay>
      ) : null}
    </DndContext>
  );
};
